import { RATE_GATE_API_BASE } from '../envVariables';
import { getSessionUid, getActivitySource } from './activitySession';

/**
 * rateGateApi — thin fetch client for the pf-comms rate-gate endpoints.
 * Pure request/response mapping only (no redux, no UI): every method resolves
 * to a plain result object and NEVER throws — network/parse failures map to
 * the error shape so callers branch on `status`/`ok` alone.
 *
 * Endpoints + response shapes per notes/public-rate-gate-structs-P1.md:
 *   POST /rate-gate/unlock          → 200 UnlockResponse | 409 GateResponse | 4xx/503
 *   POST /rate-gate/request-access  → 2xx on success
 *   POST /rate-gate/booking-event   → 2xx on success (fire-and-forget telemetry)
 */

/** @readonly @enum {string} Client-side unlock outcome (maps 1:1 onto RateGateStatus). */
export const UnlockOutcome = Object.freeze({
  UNLOCKED: 'unlocked',
  GATED: 'gated',
  ERROR: 'error',
});

const JSON_HEADERS = Object.freeze({ 'Content-Type': 'application/json' });

/** POST helper: resolves { httpStatus, data } and never rejects on JSON parse. */
const postJson = async (path, body) => {
  const response = await fetch(`${RATE_GATE_API_BASE}${path}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null; // non-JSON body (proxy error page etc.) — status code decides
  }
  return { httpStatus: response.status, data };
};

/**
 * Unlock the blurred rate cards for a public visitor.
 *
 * @param {Object} input
 * @param {string} input.email        visitor email
 * @param {string} input.captchaToken invisible reCAPTCHA response token
 * @param {Object} input.search       SearchContext (util/searchContext.buildSearchContext)
 * @returns {Promise<Object>} one of:
 *   { status:'unlocked', leadId, contactId, discount|null }
 *   { status:'gated', loginUrl|null }
 *   { status:'error', message }
 */
export const unlockRates = async ({ email, captchaToken, search }) => {
  try {
    const { httpStatus, data } = await postJson('/rate-gate/unlock', {
      email,
      captchaToken,
      search,
      // Anonymous session id so pf-comms can stitch this visitor's pre-unlock
      // searches to the Lead it creates (activity-crm D2). Best-effort.
      sessionUid: getSessionUid(),
    });

    if (httpStatus === 200 && data?.ok) {
      return {
        status: UnlockOutcome.UNLOCKED,
        leadId: data.leadId != null ? data.leadId : null,
        contactId: data.contactId != null ? data.contactId : null,
        discount: data.discount || null,
      };
    }

    if (httpStatus === 409) {
      return {
        status: UnlockOutcome.GATED,
        loginUrl: data?.loginUrl || null,
      };
    }

    // Every other shape — 4xx validation, 503 service down, 200-without-ok —
    // is a retryable client-side error state.
    return {
      status: UnlockOutcome.ERROR,
      message: (data && (data.error || data.message)) || `unlock failed (${httpStatus})`,
    };
  } catch (networkError) {
    return { status: UnlockOutcome.ERROR, message: networkError.message || 'network error' };
  }
};

/**
 * Fire-and-forget POST: resolves { ok: 2xx } and swallows network/parse errors
 * to { ok: false }. The shared shape behind request-access / booking-event /
 * events — telemetry and best-effort signals that must never throw or block UX.
 */
const fireAndForget = async (path, body) => {
  try {
    const { httpStatus } = await postJson(path, body);
    return { ok: httpStatus >= 200 && httpStatus < 300 };
  } catch {
    return { ok: false };
  }
};

/**
 * Hard-gate "Request access" — asks Prime Freight to provision portal access
 * for this email (load-bearing for gated repeat bookers, who have no login).
 * @returns {Promise<{ok: boolean}>}
 */
export const requestAccess = async ({ email }) => {
  return fireAndForget('/rate-gate/request-access', { email });
};

/**
 * Zero-result capture — the searched lane has no published rate, so ask Prime
 * Freight to quote it manually. Captures the lead + alerts sales server-side.
 * Returns a real result (the UI confirms), never throws.
 * @param {Object} input { email, captchaToken, search, note? }
 * @returns {Promise<{ok:boolean, leadId?:number|null, delivered?:boolean, error?:string}>}
 */
export const requestQuote = async ({ email, captchaToken, search, note }) => {
  try {
    // Drop null/undefined optional fields (no captcha yet, empty note) so we
    // never POST `key:null` that trips upstream optional-field validation —
    // same discipline as reportActivity.
    const body = Object.fromEntries(
      Object.entries({ email, captchaToken, search, note }).filter(([, v]) => v !== null && v !== undefined)
    );
    const { httpStatus, data } = await postJson('/rate-gate/quote-request', body);
    if (httpStatus >= 200 && httpStatus < 300 && data && data.ok) {
      return { ok: true, leadId: data.leadId ?? null, delivered: !!data.delivered };
    }
    return { ok: false, error: (data && data.error) || `quote-request responded ${httpStatus}` };
  } catch (networkError) {
    return { ok: false, error: networkError?.message || 'network error' };
  }
};

/**
 * Report a completed public booking so the CRM lead gets its BOOKING activity.
 * Fire-and-forget telemetry: never blocks or fails the booking UX.
 * @param {Object} payload { email, leadId, bookingId, payload:{ lane summary } }
 * @returns {Promise<{ok: boolean}>}
 */
export const bookingEvent = async (payload) => {
  return fireAndForget('/rate-gate/booking-event', payload);
};

/**
 * Report one client-fired ratesearch activity event (RESULT_CLICK,
 * BOOKING_STARTED, BOOKING_CREATED, RFQ_CREATED, DISCOUNT_APPLIED) to the
 * pf-comms /events ingestion, which write-throughs to the BB event stream
 * (activity-crm epic PR-2). Fire-and-forget telemetry: never blocks or fails
 * the user flow. SEARCH is NOT reported here — rate-api logs it server-side.
 * @param {Object} event { eventType, sessionUid|leadId|webuserId, ...fields }
 * @returns {Promise<{ok: boolean}>}
 */
export const reportActivity = async (event) => {
  // Drop null/undefined identity fields (e.g. sessionUid when storage is
  // blocked, leadId before unlock) so we never send `key: null` that would
  // trip the optional-field validation upstream. Mounted under /rate-gate/*
  // so it rides the same same-origin proxy as the other rate-gate calls.
  const clean = Object.fromEntries(
    Object.entries(event || {}).filter(([, v]) => v !== null && v !== undefined)
  );
  return fireAndForget('/rate-gate/events', clean);
};

/**
 * Report a BOOKING_CREATED conversion, auto-attaching the anonymous session +
 * surface so the two booking flows (public: leadId; logged-in: webuserId) share
 * one shape. Fire-and-forget via reportActivity.
 * @param {{ webuserId?: number, leadId?: number, bookingRef?: number }} refs
 */
export const reportBookingCreated = (refs) =>
  reportActivity({
    eventType: 'BOOKING_CREATED',
    sessionUid: getSessionUid(),
    source: getActivitySource(),
    ...refs,
  });

/**
 * Submit the public one-page booking through the pf-comms PROXY. The browser
 * never talks to Betty: pf-comms holds the reCAPTCHA secret + the (non-public)
 * wrapper-action id, invokes it server-side, and does the CRM wiring (BOOKING/
 * QUOTE activities + lead promotion) in the same request.
 *
 * @param {Object} payload full publicBookingPayload contract (incl. captchaToken, crm.leadId)
 * @returns {Promise<Object>} { ok, bookingId, primeFreightRef, quoteId, customerId, promoted }
 *   or { ok:false, error } — never throws.
 */
export const submitBooking = async (payload) => {
  try {
    const { httpStatus, data } = await postJson('/rate-gate/booking', payload);
    if (data && typeof data.ok === 'boolean') return data;
    return { ok: false, error: `booking proxy responded ${httpStatus}` };
  } catch (networkError) {
    return { ok: false, error: networkError?.message || 'network error' };
  }
};
